import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, vModelSelect as _vModelSelect, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal-mask"
}
const _hoisted_2 = { class: "modal-wrapper" }
const _hoisted_3 = { class: "modal-dialog modal-dialog-scrollable modal-lg" }
const _hoisted_4 = { class: "modal-content" }
const _hoisted_5 = { class: "modal-header" }
const _hoisted_6 = { class: "modal-title" }
const _hoisted_7 = { class: "modal-body" }
const _hoisted_8 = { class: "form-row" }
const _hoisted_9 = { class: "form-group col" }
const _hoisted_10 = { class: "form-group col" }
const _hoisted_11 = ["disabled"]
const _hoisted_12 = ["value"]
const _hoisted_13 = {
  key: 0,
  class: "form-text text-muted"
}
const _hoisted_14 = { class: "form-group" }
const _hoisted_15 = { class: "form-row" }
const _hoisted_16 = { class: "form-group col" }
const _hoisted_17 = { class: "form-group col" }
const _hoisted_18 = { class: "form-group" }
const _hoisted_19 = ["value"]
const _hoisted_20 = { class: "form-group" }
const _hoisted_21 = ["value"]
const _hoisted_22 = { class: "form-group" }
const _hoisted_23 = ["value"]
const _hoisted_24 = { class: "form-group" }
const _hoisted_25 = ["value"]

import { computed, watch, onMounted, ref, type Ref } from 'vue'
    import { EditorState, generateName } from "@common/Common"
    import { guid } from "@common/Guid"

    import
    {
        useNewScenarioSettingsQueryV1,
        useExistingScenarioSettingsQueryV1
    } from "@Quest/PortfolioScenarios/queries-vue-composables"

    import
    {
        useCreatePortfolioScenarioCommandV1,
        useUpdatePortfolioScenarioCommandV1
    } from "@Quest/PortfolioScenarios/commands-vue-composables"

    import type {
        PortfolioScenarioSettingsReadModel,
        WeightsSetReadModel
    } from "@Quest/PortfolioScenarios/readmodels"

    import type { PortfolioScenarioEditorViewModel } from "@Quest/PortfolioScenarios/Components/PortfolioScenarioEditor"
    import { CreatePortfolioScenarioCommandV1, UpdatePortfolioScenarioCommandV1 } from '../commands-core'


    // ======================
    // Props
    // ======================

    
export default /*@__PURE__*/_defineComponent({
  __name: 'PortfolioScenarioEditor',
  props: {
    viewModel: {}
  },
  emits: ["scenario-created", "scenario-updated"],
  setup(__props: any, { emit: __emit }) {

    const props = __props

    const emit = __emit

    const vm = props.viewModel

    // ======================
    // Derived props
    // ======================

    const opened = computed(() => vm?.opened ?? false)

    const workspaceId = computed(() => vm.workspaceId)
    const portfolioId = computed(() => vm.portfolioId)
    const portfolioScenarioId = computed(() => vm.portfolioScenarioId)

    const corporateScenarioId = computed({
        get: () => vm.corporateScenarioId,
        set: (val: string) => vm.corporateScenarioId = val
    })

    // ======================
    // Queries
    // ======================

    const {
        runNewScenarioSettingsQueryV1,
        modelNewScenarioSettingsQueryV1
    } = useNewScenarioSettingsQueryV1(workspaceId, portfolioId)

    const {
        runExistingScenarioSettingsQueryV1,
        modelExistingScenarioSettingsQueryV1
    } = useExistingScenarioSettingsQueryV1(workspaceId, portfolioId)

    const rm = computed<PortfolioScenarioSettingsReadModel | null>(() =>
    {
        if (vm.state === EditorState.Creator)
        {
            return (modelNewScenarioSettingsQueryV1 as Ref<PortfolioScenarioSettingsReadModel | null>).value
        }

        if (vm.state === EditorState.Editor || vm.state === EditorState.Cloner)
        {
            return (modelExistingScenarioSettingsQueryV1 as Ref<PortfolioScenarioSettingsReadModel | null>).value
        }

        return null
    })

    // ======================
    // Local State
    // ======================

    const criteriaHierarchyId = ref<number | null>(null)
    const weightsSetId = ref<number | null>(null)

    const weightsSets = computed<WeightsSetReadModel[]>(() =>
    {
        return rm.value?.ValueDefinitions
            .find(a => a.Id === criteriaHierarchyId.value)
            ?.WeightsSets ?? []
    })

    // ======================
    // Commands
    // ======================

    const {
        invokeCreatePortfolioScenarioCommandV1,
        onSucceedCreatePortfolioScenarioCommandV1,
        command: createPortfolioScenarioCommandV1
    } = useCreatePortfolioScenarioCommandV1(workspaceId, corporateScenarioId, portfolioId)

    const {
        invokeUpdatePortfolioScenarioCommandV1,
        onSucceedUpdatePortfolioScenarioCommandV1,
        command: updatePortfolioScenarioCommandV1
    } = useUpdatePortfolioScenarioCommandV1(workspaceId, corporateScenarioId, portfolioId)

    // ======================
    // Watchers
    // ======================

    // Opened watcher (replaces onOpenedChanged)
    watch(opened, (val) =>
    {
        if (val === true)
        {
            if (vm.state === EditorState.Creator)
            {
                runNewScenarioSettingsQueryV1({ CorporateScenarioId: corporateScenarioId.value })
            }
            else if (vm.state === EditorState.Editor || vm.state === EditorState.Cloner)
            {
                runExistingScenarioSettingsQueryV1({ PortfolioScenarioId: portfolioScenarioId.value })
            }
        }
    })

    // Criteria hierarchy watcher (replaces onCriteriaHierarchyIdChanged)
    watch(criteriaHierarchyId, () =>
    {
        if (!weightsSetId.value) return

        if (!weightsSets.value.some(a => a.Id === weightsSetId.value))
        {
            weightsSetId.value = null
        }
    })

    watch(rm, (current) =>
    {
        if (!current) return

        if (vm.state === EditorState.Cloner)
        {
            current.Name = generateName(current.Name)
        }

        criteriaHierarchyId.value = current.CriteriaHierarchyId
        weightsSetId.value = current.WeightsSetId
        corporateScenarioId.value = current.CorporateScenarioId
    }, { immediate: true })

    onSucceedUpdatePortfolioScenarioCommandV1(() =>
    {
        const cmd = updatePortfolioScenarioCommandV1.value as UpdatePortfolioScenarioCommandV1

        emit('scenario-updated', cmd.Id)
        close()
    })

    onSucceedCreatePortfolioScenarioCommandV1(() =>
    {
        const cmd = createPortfolioScenarioCommandV1.value as CreatePortfolioScenarioCommandV1

        emit('scenario-created', cmd.Id)
        close()
    })

    // ======================
    // Methods
    // ======================

    function onFormSubmit()
    {
        if (!rm.value) return

        if (vm.state === EditorState.Creator)
        {
            invokeCreatePortfolioScenarioCommandV1({
                Id: guid(),
                Name: rm.value.Name,
                Description: rm.value.Description,
                CriteriaHierarchyId: criteriaHierarchyId.value,
                WeightsSetId: weightsSetId.value,
                ApplyBenefitsConfidenceFactor: rm.value.ApplyBenefitsConfidenceFactor,
                Denomination: rm.value.Denomination,
                ApprovalAcceptedStatusId: rm.value.ApprovalAcceptedStatusId,
                ApprovalRejectedStatusId: rm.value.ApprovalRejectedStatusId
            })
        }
        else if (vm.state === EditorState.Editor)
        {
            invokeUpdatePortfolioScenarioCommandV1({
                Id: rm.value.Id,
                Name: rm.value.Name,
                Description: rm.value.Description,
                CriteriaHierarchyId: criteriaHierarchyId.value,
                WeightsSetId: weightsSetId.value,
                ApplyBenefitsConfidenceFactor: rm.value.ApplyBenefitsConfidenceFactor,
                Denomination: rm.value.Denomination,
                ApprovalAcceptedStatusId: rm.value.ApprovalAcceptedStatusId,
                ApprovalRejectedStatusId: rm.value.ApprovalRejectedStatusId
            })
        }
    }

    function close()
    {
        vm.close()
    }

return (_ctx: any,_cache: any) => {
  return (opened.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("h5", _hoisted_6, _toDisplayString(_unref(vm).modalTitle), 1 /* TEXT */),
                _createElementVNode("button", {
                  onClick: close,
                  type: "button",
                  class: "close"
                }, "×")
              ]),
              _createElementVNode("div", _hoisted_7, [
                (rm.value)
                  ? (_openBlock(), _createElementBlock("form", {
                      key: 0,
                      onSubmit: _withModifiers(onFormSubmit, ["prevent"]),
                      id: "settingsForm"
                    }, [
                      _createElementVNode("div", _hoisted_8, [
                        _createElementVNode("div", _hoisted_9, [
                          _cache[9] || (_cache[9] = _createElementVNode("label", null, "Plan name:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("input", {
                            type: "text",
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((rm.value.Name) = $event)),
                            class: "form-control",
                            required: "",
                            minlength: "2"
                          }, null, 512 /* NEED_PATCH */), [
                            [
                              _vModelText,
                              rm.value.Name,
                              void 0,
                              { trim: true }
                            ]
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                          _cache[11] || (_cache[11] = _createElementVNode("label", null, "Link to Enterprise Plan:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("select", {
                            class: "form-control",
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((corporateScenarioId).value = $event)),
                            disabled: rm.value.IsActive
                          }, [
                            _cache[10] || (_cache[10] = _createElementVNode("option", {
                              value: null,
                              disabled: ""
                            }, "Choose...", -1 /* CACHED */)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rm.value.CorporateScenarios, (cs) => {
                              return (_openBlock(), _createElementBlock("option", {
                                value: cs.Id
                              }, _toDisplayString(cs.Name), 9 /* TEXT, PROPS */, _hoisted_12))
                            }), 256 /* UNKEYED_FRAGMENT */))
                          ], 8 /* PROPS */, _hoisted_11), [
                            [_vModelSelect, corporateScenarioId.value]
                          ]),
                          (rm.value.IsActive)
                            ? (_openBlock(), _createElementBlock("small", _hoisted_13, " This cannot be changed because the plan is currently active in that enterprise plan. "))
                            : _createCommentVNode("v-if", true)
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_14, [
                        _cache[12] || (_cache[12] = _createElementVNode("label", null, "Description:", -1 /* CACHED */)),
                        _withDirectives(_createElementVNode("textarea", {
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((rm.value.Description) = $event)),
                          class: "form-control"
                        }, null, 512 /* NEED_PATCH */), [
                          [
                            _vModelText,
                            rm.value.Description,
                            void 0,
                            { trim: true }
                          ]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_15, [
                        _createElementVNode("div", _hoisted_16, [
                          _cache[14] || (_cache[14] = _createElementVNode("label", { for: "denomination" }, "Currency denomination:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("select", {
                            id: "denomination",
                            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((rm.value.Denomination) = $event)),
                            class: "form-control"
                          }, [...(_cache[13] || (_cache[13] = [
                            _createElementVNode("option", { value: "Unit" }, "Unit", -1 /* CACHED */),
                            _createElementVNode("option", { value: "Thousand" }, "Thousand", -1 /* CACHED */),
                            _createElementVNode("option", { value: "Million" }, "Million", -1 /* CACHED */)
                          ]))], 512 /* NEED_PATCH */), [
                            [_vModelSelect, rm.value.Denomination]
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_17, [
                          _cache[16] || (_cache[16] = _createElementVNode("label", null, "Risk adjusted benefits:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("select", {
                            class: "form-control",
                            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((rm.value.ApplyBenefitsConfidenceFactor) = $event))
                          }, [...(_cache[15] || (_cache[15] = [
                            _createElementVNode("option", { value: true }, "Enabled", -1 /* CACHED */),
                            _createElementVNode("option", { value: false }, "Disabled", -1 /* CACHED */)
                          ]))], 512 /* NEED_PATCH */), [
                            [_vModelSelect, rm.value.ApplyBenefitsConfidenceFactor]
                          ])
                        ])
                      ]),
                      _createElementVNode("fieldset", null, [
                        _cache[21] || (_cache[21] = _createElementVNode("legend", null, "Value definition", -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_18, [
                          _cache[18] || (_cache[18] = _createElementVNode("label", null, "Goal:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("select", {
                            class: "form-control",
                            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((criteriaHierarchyId).value = $event))
                          }, [
                            _cache[17] || (_cache[17] = _createElementVNode("option", { value: null }, "Choose...", -1 /* CACHED */)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rm.value.ValueDefinitions, (ch) => {
                              return (_openBlock(), _createElementBlock("option", {
                                value: ch.Id
                              }, _toDisplayString(ch.Name), 9 /* TEXT, PROPS */, _hoisted_19))
                            }), 256 /* UNKEYED_FRAGMENT */))
                          ], 512 /* NEED_PATCH */), [
                            [_vModelSelect, criteriaHierarchyId.value]
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_20, [
                          _cache[20] || (_cache[20] = _createElementVNode("label", null, "Weights set:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("select", {
                            class: "form-control",
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((weightsSetId).value = $event))
                          }, [
                            _cache[19] || (_cache[19] = _createElementVNode("option", { value: null }, "Default", -1 /* CACHED */)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weightsSets.value, (ws) => {
                              return (_openBlock(), _createElementBlock("option", {
                                value: ws.Id
                              }, _toDisplayString(ws.Name), 9 /* TEXT, PROPS */, _hoisted_21))
                            }), 256 /* UNKEYED_FRAGMENT */))
                          ], 512 /* NEED_PATCH */), [
                            [_vModelSelect, weightsSetId.value]
                          ])
                        ])
                      ]),
                      _createElementVNode("fieldset", null, [
                        _cache[28] || (_cache[28] = _createElementVNode("legend", null, "Approval", -1 /* CACHED */)),
                        _createElementVNode("div", _hoisted_22, [
                          _cache[23] || (_cache[23] = _createElementVNode("label", null, "Accepted:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("select", {
                            class: "form-control",
                            "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((rm.value.ApprovalAcceptedStatusId) = $event))
                          }, [
                            _cache[22] || (_cache[22] = _createElementVNode("option", { value: null }, "Do not change", -1 /* CACHED */)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rm.value.Statuses, (s) => {
                              return (_openBlock(), _createElementBlock("option", {
                                value: s.Id
                              }, _toDisplayString(s.Name), 9 /* TEXT, PROPS */, _hoisted_23))
                            }), 256 /* UNKEYED_FRAGMENT */))
                          ], 512 /* NEED_PATCH */), [
                            [_vModelSelect, rm.value.ApprovalAcceptedStatusId]
                          ]),
                          _cache[24] || (_cache[24] = _createElementVNode("small", { class: "form-text text-muted" }, " Kanban status for Accepted projects. The Kanban status will be updated when you Approve your decision in the scenario. ", -1 /* CACHED */))
                        ]),
                        _createElementVNode("div", _hoisted_24, [
                          _cache[26] || (_cache[26] = _createElementVNode("label", null, "Rejected:", -1 /* CACHED */)),
                          _withDirectives(_createElementVNode("select", {
                            class: "form-control",
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((rm.value.ApprovalRejectedStatusId) = $event))
                          }, [
                            _cache[25] || (_cache[25] = _createElementVNode("option", { value: null }, "Do not change", -1 /* CACHED */)),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rm.value.Statuses, (s) => {
                              return (_openBlock(), _createElementBlock("option", {
                                value: s.Id
                              }, _toDisplayString(s.Name), 9 /* TEXT, PROPS */, _hoisted_25))
                            }), 256 /* UNKEYED_FRAGMENT */))
                          ], 512 /* NEED_PATCH */), [
                            [_vModelSelect, rm.value.ApprovalRejectedStatusId]
                          ]),
                          _cache[27] || (_cache[27] = _createElementVNode("small", { class: "form-text text-muted" }, " Kanban status for Rejected projects. The Kanban status will be updated when you Approve your decision in the scenario. ", -1 /* CACHED */))
                        ])
                      ])
                    ], 32 /* NEED_HYDRATION */))
                  : _createCommentVNode("v-if", true)
              ]),
              _createElementVNode("div", { class: "modal-footer" }, [
                _createElementVNode("button", {
                  onClick: close,
                  type: "button",
                  class: "btn btn-outline-secondary"
                }, "Cancel"),
                _cache[29] || (_cache[29] = _createElementVNode("button", {
                  type: "submit",
                  form: "settingsForm",
                  class: "btn btn-primary"
                }, "Confirm", -1 /* CACHED */))
              ])
            ])
          ])
        ])
      ]))
    : _createCommentVNode("v-if", true)
}
}

})